import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { safeLocalStorage } from '@/lib/storage/safe-storage';
import { getThreadUuid } from '@/lib/utils/thread';

const THREAD_STATUS_STORAGE_KEY = 'bazak-thread-status';

export type ThreadStatus = 'streaming' | 'awaiting-approval' | 'error' | 'unread';

interface ThreadStatusState {
  statuses: Record<string, ThreadStatus>;
  activeThreadId: string | null;
  /**
   * Last live-activity timestamp per durable thread uuid (run start/end). The sidebar recency
   * sort folds this in via `max(lastMessageAt, lastActivityAt)`: a thread's `lastMessageAt` comes
   * from boot metadata and is NOT bumped when a new message streams, so continuing an old thread
   * mid-session wouldn't re-float it without this live signal.
   */
  activityAt: Record<string, number>;
}

interface ThreadStatusActions {
  markRunStart: (threadId: string) => void;
  markRunEnd: (threadId: string) => void;
  markError: (threadId: string) => void;
  setAwaitingApproval: (options: { threadId: string; awaiting: boolean }) => void;
  setActiveThread: (threadId: string | null) => void;
  clearStatus: (threadId: string) => void;
}

export type ThreadStatusStore = ThreadStatusState & ThreadStatusActions;

function withStatus({
  statuses,
  threadId,
  status,
}: {
  statuses: Record<string, ThreadStatus>;
  threadId: string;
  status: ThreadStatus | undefined;
}): Record<string, ThreadStatus> {
  if (statuses[threadId] === status) return statuses;
  const next = { ...statuses };
  if (status === undefined) delete next[threadId];
  else next[threadId] = status;
  return next;
}

export const useThreadStatusStore = create<ThreadStatusStore>()(
  persist(
    set => ({
      statuses: {},
      activeThreadId: null,
      activityAt: {},

      markRunStart: threadId => {
        set(state => ({
          statuses: withStatus({ statuses: state.statuses, threadId, status: 'streaming' }),
          activityAt: { ...state.activityAt, [threadId]: Date.now() },
        }));
      },

      // 'awaiting-approval' and 'error' outlive the run: the paused/failed state is what the
      // indicator should keep showing, and both are set by seams that race with runEnd.
      markRunEnd: threadId =>
        set(state => {
          const activityAt = { ...state.activityAt, [threadId]: Date.now() };
          const current = state.statuses[threadId];
          if (current === 'awaiting-approval' || current === 'error') return { activityAt };
          const next = threadId === state.activeThreadId ? undefined : 'unread';
          return {
            statuses: withStatus({ statuses: state.statuses, threadId, status: next }),
            activityAt,
          };
        }),

      markError: threadId => {
        set(state => ({
          statuses: withStatus({ statuses: state.statuses, threadId, status: 'error' }),
        }));
      },

      setAwaitingApproval: ({ threadId, awaiting }) =>
        set(state => {
          if (awaiting) {
            return {
              statuses: withStatus({
                statuses: state.statuses,
                threadId,
                status: 'awaiting-approval',
              }),
            };
          }
          if (state.statuses[threadId] !== 'awaiting-approval') return state;
          return {
            statuses: withStatus({ statuses: state.statuses, threadId, status: undefined }),
          };
        }),

      setActiveThread: threadId =>
        set(state => ({
          activeThreadId: threadId,
          statuses:
            threadId && state.statuses[threadId] === 'unread'
              ? withStatus({ statuses: state.statuses, threadId, status: undefined })
              : state.statuses,
        })),

      clearStatus: threadId =>
        set(state => ({
          statuses: withStatus({ statuses: state.statuses, threadId, status: undefined }),
        })),
    }),
    {
      name: THREAD_STATUS_STORAGE_KEY,
      storage: createJSONStorage(() => ({
        getItem: key => safeLocalStorage.getItem(key),
        setItem: (key, value) => safeLocalStorage.setItem(key, value),
        removeItem: key => safeLocalStorage.removeItem(key),
      })),
      // activeThreadId is session-fresh (set on load from the restored thread); only the per-thread
      // dot state and recency timestamps persist across a refresh.
      partialize: state => ({ statuses: state.statuses, activityAt: state.activityAt }),
      merge: (persisted, current) => {
        if (typeof persisted !== 'object' || persisted === null) return current;
        const { statuses = {}, activityAt = {} } = persisted as Pick<
          ThreadStatusState,
          'statuses' | 'activityAt'
        >;
        // A thread that was 'streaming' when the tab closed is, on reload, a finished-but-unseen
        // reply — not live (resumable tenants re-arm 'streaming' via markRunStart when the resume
        // reconnects). Downgrade to 'unread' so no stale spinner survives the refresh.
        const rehydrated: Record<string, ThreadStatus> = {};
        for (const [key, status] of Object.entries(statuses)) {
          rehydrated[key] = status === 'streaming' ? 'unread' : status;
        }
        return { ...current, statuses: rehydrated, activityAt };
      },
    },
  ),
);

/**
 * The store's key space is the durable thread uuid: `remoteId` once assigned, which both
 * persistence modes mint via `getOrCreateThreadUuid` — so the transport's `memory.thread`
 * uuid, the resumable record's `threadUuid`, and the row's `remoteId` all resolve to the
 * same key. For not-yet-initialized local threads the mapper yields that future remoteId.
 */
export function resolveThreadStatusKey(item: { id: string; remoteId?: string }): string {
  return item.remoteId ?? getThreadUuid(item.id);
}
